import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useFocusEffect, useNavigation } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type EnumItem = {
    enumerationId: string;
    code: string;
    value: string;
    sequence: number;
    parameter?: string | number | null;
};

type TotalWorshipsModel = {
    totalMinutes: number;
    totalMarks: number;
    minutesOfWorships: string;
    marksOfWorships: number;
    readingContent?: string | null;
    existingEnumValue?: number | null;
    isEditable: boolean;
};

const MIN_REQUIRED_MINUTES = 30;
const MARKS_PER_BLOCK = 6;
const MINUTES_PER_BLOCK = 30;

const timeOnlyToMinutes = (time: string | null | undefined) => {
    if (!time) return 0;

    const parts = time.split(":");
    if (parts.length < 2) return 0;

    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;

    return hours * 60 + minutes;
};

const onlyDigits = (s: string) => (s || "").replace(/[^\d]/g, "");

const minutesToTimeOnlyString = (mins: number) => {
    const safe = Number.isFinite(mins) && mins >= 0 ? Math.floor(mins) : 0;
    const h = Math.floor(safe / 60);
    const m = safe % 60;

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${hh}:${mm}:00`;
};

const calculateReadingMarks = (mins: number) => {
    if (mins < MIN_REQUIRED_MINUTES) return 0;

    const blocks = Math.floor(mins / MINUTES_PER_BLOCK);
    return blocks * MARKS_PER_BLOCK;
};

export function ReadingScreen() {
    const navigation = useNavigation<any>();
    const { user } = useAuth();

    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    const [date, setDate] = useState(todayStr);

    const [loading, setLoading] = useState(false);

    const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);
    const [worshipTypeLabel, setWorshipTypeLabel] = useState<string>("Reading");

    const [minutes, setMinutes] = useState<string>("0");
    const [readingContent, setReadingContent] = useState<string>("");

    const [text, setText] = useState("");
    const [marks, setMarks] = useState(0);
    const isSaveDisabled = marks < 5;

    const minutesValidationError = useMemo(
        () => getMinutesValidationError(minutes, MIN_REQUIRED_MINUTES),
        [minutes]
    );
    const minutesNum = useMemo(() => parseValidMinutes(minutes) ?? 0, [minutes]);

    const calculatedMarks = useMemo(() => {
        return calculateReadingMarks(minutesNum);
    }, [minutesNum]);

    const isMinutesValid = !minutesValidationError;

    const [totalMinutes, setTotalMinutes] = useState(0);
    const [totalMarks, setTotalMarks] = useState(0);
    const [isEditable, setIsEditable] = useState(true);

    const minutesPerMarkText = useMemo(() => "30 Minutes = 6 Marks", []);
    const marksRuleText = useMemo(
        () => "30-59 min = 6, 60-89 min = 12, 90-119 min = 18",
        []
    );

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    setDate(format(selectedDate, "yyyy-MM-dd"));
                }
            },
        });
    };

    const handleChange = (value: string) => {
        setText(value);
        const length = value.trim().length;
        const calculatedMarks = Math.floor(length / 10) * 5;
        setMarks(calculatedMarks);
    };

    const loadEnumerationAndSetReading = useCallback(async () => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getEnumeration("WorshipType");
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load worship types");
                return;
            }

            const list = (res?.data || []) as EnumItem[];
            const reading = list.find(
                (x) => (x.value || "").trim().toLowerCase() === "reading"
            );

            if (!reading?.enumerationId) {
                Flash.error("Reading worship type not found in Enumeration");
                return;
            }

            setWorshipTypeId(reading.enumerationId);
            setWorshipTypeLabel(reading.value || "Reading");
        } catch (e: any) {
            Flash.error(e?.message || "Enumeration load error");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAgainstDate = useCallback(async (dateStr: string, wTypeId: string) => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getWorshipAgainstDate({
                date: dateStr,
                worshipType: wTypeId,
            });

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load reading data");
                return;
            }

            const data = (res?.data || null) as TotalWorshipsModel | null;

            setTotalMinutes(data?.totalMinutes ?? 0);
            setTotalMarks(data?.totalMarks ?? 0);
            setIsEditable(data?.isEditable ?? true);

            const mins = timeOnlyToMinutes(data?.minutesOfWorships);
            setMinutes(String(mins));
            setReadingContent((data?.readingContent ?? "") as string);
        } catch (e: any) {
            Flash.error(e?.message || "Load failed");
        } finally {
            setLoading(false);
        }
    }, []);

    const init = useCallback(async () => {
        await loadEnumerationAndSetReading();
    }, [loadEnumerationAndSetReading]);

    useEffect(() => {
        init();
    }, [init]);

    useFocusEffect(
        useCallback(() => {
            if (!worshipTypeId) return;
            loadAgainstDate(date, worshipTypeId);
        }, [date, worshipTypeId, loadAgainstDate])
    );

    const onSave = async () => {
        try {
            if (!user?.userId) {
                Flash.error("User not found");
                return;
            }
            if (!worshipTypeId) {
                Flash.error("Worship type not ready");
                return;
            }
            if (minutesValidationError) {
                Flash.error(minutesValidationError);
                return;
            }

            setLoading(true);

            const minutesTimeOnly = minutesToTimeOnlyString(minutesNum);

            const payload = {
                worshipId: null,
                userId: user.userId,
                date,
                worshipType: worshipTypeId,
                minutes: minutesTimeOnly,
                marks: calculatedMarks,
                readingContent: readingContent?.trim() ? readingContent.trim() : null,
            };

            const res = await TimeBasedService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to save");
                return;
            }

            Flash.success("Success!", "Reading Saved Successfully!");
            navigation.navigate("ReadingHistoryScreen");
        } catch (e: any) {
            Flash.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header title={worshipTypeLabel} showBack />

                <ScrollView
                    contentContainerStyle={styles.container}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryTitle}>This Month&apos;s Offering</Text>

                        <View style={styles.summarySplit}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.summaryMini}>TOTAL MINUTES</Text>
                                <Text style={styles.summaryValue}>{totalMinutes}</Text>
                            </View>

                            <View style={{ flex: 1, alignItems: "center" }}>
                                <View style={styles.divider} />
                            </View>

                            <View style={{ flex: 1, alignItems: "flex-start" }}>
                                <Text style={styles.summaryMini}>TOTAL MARKS</Text>
                                <Text style={styles.summaryValue}>{totalMarks}</Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.card}>
                        <View style={styles.topRow}>
                            <Pressable onPress={openDatePicker} style={styles.dateChip}>
                                <Text style={styles.dateChipIcon}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={() => navigation.navigate("ReadingHistoryScreen")}
                                style={styles.historyBtn}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <View style={{ marginTop: 14, gap: 10 }}>
                            <Input
                                label="Number of Minutes *"
                                keyboardType="number-pad"
                                value={minutes}
                                onChangeText={(t) => setMinutes(onlyDigits(t))}
                                containerStyle={{ gap: 8 }}
                            />

                            {minutesValidationError ? (
                                <Text style={styles.validationText}>
                                    {minutesValidationError}
                                </Text>
                            ) : null}


                            <Input
                                label="Reading Content"
                                value={readingContent}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) {
                                        setReadingContent(text);
                                    }
                                }}
                                placeholder="e.g. Nectar of Devotion..."
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                containerStyle={{ gap: 8 }}
                            />

                            <Text style={styles.charCount}>
                                {readingContent.length}/1000
                            </Text>

                            <Text style={styles.helperText}>{minutesPerMarkText}</Text>

                            <View style={styles.calcRow}>
                                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                                <Text style={styles.calcValue}>{calculatedMarks}</Text>
                            </View>

                            {isEditable && (
                                <View style={styles.btnRow}>
                                    <Pressable
                                        onPress={() => navigation.goBack()}
                                        disabled={loading}
                                        style={({ pressed }) => [
                                            styles.cancelBtn,
                                            pressed ? { opacity: 0.95 } : null,
                                            loading ? { opacity: 0.6 } : null,
                                        ]}
                                    >
                                        <Text style={styles.cancelText}>Cancel</Text>
                                    </Pressable>

                                    <Pressable
                                        disabled={loading || !isMinutesValid}
                                        onPress={onSave}
                                        style={{ flex: 1 }}
                                    >
                                        <LinearGradient
                                            colors={["#2563EB", "#7C3AED"]}
                                            start={{ x: 0, y: 0.5 }}
                                            end={{ x: 1, y: 0.5 }}
                                            style={[
                                                styles.saveBtn,
                                                loading || !isMinutesValid ? { opacity: 0.75 } : null,
                                            ]}
                                        >
                                            <Text style={styles.saveText}>
                                                {loading ? "Saving..." : "Save"}
                                            </Text>
                                        </LinearGradient>
                                    </Pressable>
                                </View>
                            )}

                            {loading ? (
                                <View style={{ marginTop: 10, alignItems: "center" }}>
                                    <ActivityIndicator />
                                </View>
                            ) : null}
                        </View>
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, paddingBottom: 30, gap: 12 },

    summaryCard: {
        borderRadius: 14,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.82)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.15)",
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 8 },
        elevation: 3,
    },
    summaryTitle: { fontSize: 13, fontWeight: "900", color: "rgba(17,24,39,0.85)" },

    summarySplit: { flexDirection: "row", alignItems: "center", marginTop: 10 },
    summaryMini: { fontSize: 10, fontWeight: "900", color: "rgba(0,0,0,0.35)", letterSpacing: 0.7 },
    summaryValue: { marginTop: 4, fontSize: 20, fontWeight: "900", color: "#1D4ED8" },

    divider: { width: 1, height: 40, backgroundColor: "rgba(0,0,0,0.06)" },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.90)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        shadowColor: "#000",
        shadowOpacity: 0.10,
        shadowRadius: 14,
        shadowOffset: { width: 0, height: 10 },
        elevation: 4,
    },

    topRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(37,99,235,0.08)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.18)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },
    dateChipIcon: { fontSize: 14, opacity: 0.9 },
    dateChipText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.85)" },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingHorizontal: 10,
        height: 34,
        borderRadius: 10,
        backgroundColor: "rgba(37,99,235,0.06)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.12)",
    },
    historyIcon: { fontSize: 14 },
    historyText: { fontSize: 12, fontWeight: "900", color: "#1D4ED8" },

    validationText: {
        marginTop: -4,
        fontSize: 11,
        fontWeight: "800",
        color: "#DC2626",
    },

    helperText: {
        marginTop: -2,
        fontSize: 11,
        fontWeight: "800",
        color: "rgba(17,24,39,0.55)",
    },

    ruleText: {
        marginTop: -2,
        fontSize: 11,
        fontWeight: "700",
        color: "rgba(17,24,39,0.45)",
    },

    charCount: {
        fontSize: 11,
        color: "rgba(0,0,0,0.4)",
        textAlign: "right",
    },

    calcRow: {
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: "rgba(37,99,235,0.06)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.12)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    calcLabel: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.60)" },
    calcValue: { fontSize: 18, fontWeight: "900", color: "#1D4ED8" },

    btnRow: { flexDirection: "row", gap: 10, marginTop: 6 },

    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.10)",
        backgroundColor: "rgba(255,255,255,0.95)",
        alignItems: "center",
        justifyContent: "center",
    },
    cancelText: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.55)" },

    saveBtn: {
        height: 46,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 4,
    },
    saveText: { fontSize: 14, fontWeight: "900", color: "#fff" },
});
